import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Button } from '@app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@app/components/ui/dropdown-menu';
import { Input } from '@app/components/ui/input';
import { cn } from '@app/lib/utils';
import type { TeamWithoutMembersDTO } from '@shared/dto/teams';

import { Check, ChevronDown, Search, Users } from 'lucide-react';

import { useTeamsContext } from '../contexts/TeamsContext';

const TEAM_MENU_ATTR = 'data-team-menu';
const TEAM_MENU_CLOSE_DELAY_MS = 120;
// Loading a new team's permissions can unmount the entire app shell, including the old trigger.
let pendingTeamTriggerFocus: string | null = null;

function isTeamMenuElement(target: EventTarget | null) {
  return target instanceof Element && target.closest(`[${TEAM_MENU_ATTR}="true"]`) !== null;
}

export function TeamMenu() {
  const { currentTeam, teams, setCurrentTeam, isLoading } = useTeamsContext();
  const currentTeamId = currentTeam?.id;
  const menuInstanceId = useId().replaceAll(':', '');
  const teamButtonId = `team-button-${menuInstanceId}`;
  const teamMenuId = `team-menu-${menuInstanceId}`;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const closeTimeoutRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const teamListRef = useRef<HTMLDivElement>(null);
  const focusSearchOnOpenRef = useRef(false);
  const tabDismissedRef = useRef(false);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(true);
  }, [clearCloseTimeout]);

  const closeMenu = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(false);
    setSearchQuery('');
  }, [clearCloseTimeout]);

  const scheduleCloseMenu = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setIsOpen(false);
      setSearchQuery('');
    }, TEAM_MENU_CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const handleMenuMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (isTeamMenuElement(event.relatedTarget)) {
        return;
      }

      scheduleCloseMenu();
    },
    [scheduleCloseMenu],
  );

  const handleMenuPointerLeave = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (isTeamMenuElement(event.relatedTarget)) {
        return;
      }

      scheduleCloseMenu();
    },
    [scheduleCloseMenu],
  );

  const handleDocumentHover = useCallback(
    (event: MouseEvent | PointerEvent) => {
      if (isTeamMenuElement(event.target)) {
        clearCloseTimeout();
        return;
      }

      scheduleCloseMenu();
    },
    [clearCloseTimeout, scheduleCloseMenu],
  );

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }

    document.addEventListener('mousemove', handleDocumentHover);
    document.addEventListener('pointermove', handleDocumentHover);

    return () => {
      document.removeEventListener('mousemove', handleDocumentHover);
      document.removeEventListener('pointermove', handleDocumentHover);
    };
  }, [handleDocumentHover, isOpen]);

  useEffect(() => {
    return clearCloseTimeout;
  }, [clearCloseTimeout]);

  useEffect(() => {
    if (!isOpen || !focusSearchOnOpenRef.current) {
      return;
    }

    focusSearchOnOpenRef.current = false;
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!currentTeamId || pendingTeamTriggerFocus !== currentTeamId) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (pendingTeamTriggerFocus === currentTeamId && triggerRef.current) {
        pendingTeamTriggerFocus = null;
        triggerRef.current.focus();
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [currentTeamId]);

  // Sort teams alphabetically and filter by search query.
  const sortedAndFilteredTeams = useMemo(() => {
    if (!teams) {
      return [];
    }

    const sorted = [...teams].sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );

    if (!searchQuery.trim()) {
      return sorted;
    }

    const query = searchQuery.toLowerCase().trim();
    return sorted.filter((team) => team.name.toLowerCase().includes(query));
  }, [teams, searchQuery]);

  if (isLoading || !teams || teams.length === 0) {
    return null;
  }

  const handleTeamSwitch = (team: TeamWithoutMembersDTO) => {
    if (team.id !== currentTeam?.id) {
      pendingTeamTriggerFocus = team.id;
    }
    setCurrentTeam(team);
    closeMenu();
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    setSearchQuery('');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const leaveMenuWithTab = (event: React.KeyboardEvent<HTMLElement>) => {
    event.stopPropagation();
    tabDismissedRef.current = true;
    triggerRef.current?.focus();
    closeMenu();
    // Let the browser move forward from the trigger to the next control in the page.
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      return;
    }

    // Typing belongs to the input rather than the menu's typeahead search.
    event.stopPropagation();

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Tab') {
      const items = teamListRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
      if (event.key === 'Tab' && event.shiftKey) {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key === 'Tab' && !items?.length) {
        leaveMenuWithTab(event);
        return;
      }

      event.preventDefault();
      const item = event.key === 'ArrowUp' ? items?.[items.length - 1] : items?.[0];
      item?.focus();
    }
  };

  return (
    <DropdownMenu modal={false} open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          id={teamButtonId}
          ref={triggerRef}
          type="button"
          variant="ghost"
          data-testid="team-menu-button"
          data-team-menu="true"
          aria-controls={isOpen ? teamMenuId : undefined}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onKeyDown={(event) => {
            if (!isOpen && ['Enter', ' ', 'ArrowDown'].includes(event.key)) {
              focusSearchOnOpenRef.current = true;
            }
          }}
          onMouseEnter={openMenu}
          onMouseMove={openMenu}
          onMouseOver={openMenu}
          onMouseLeave={handleMenuMouseLeave}
          onPointerEnter={openMenu}
          onPointerMove={openMenu}
          onPointerOver={openMenu}
          onPointerLeave={handleMenuPointerLeave}
          className={cn(
            'w-9 min-w-0 justify-center px-0 text-foreground transition-colors sm:w-auto sm:max-w-[140px] sm:justify-start sm:px-3 sm:pr-4 md:max-w-[160px] lg:max-w-[180px]',
            isOpen && 'z-(--z-dropdown) rounded-b-none bg-popover',
          )}
        >
          <Users className="size-4 shrink-0" />
          <span
            className="sr-only sm:not-sr-only sm:min-w-0 sm:truncate sm:text-left"
            data-testid="team-menu-current-team"
          >
            {currentTeam?.name || 'My Team'}
          </span>
          <ChevronDown
            className={cn(
              'ml-auto hidden size-4 shrink-0 opacity-50 transition-transform duration-300 sm:block',
              isOpen && 'rotate-180',
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        id={teamMenuId}
        aria-labelledby={teamButtonId}
        data-team-menu="true"
        align="start"
        sideOffset={7}
        onMouseEnter={openMenu}
        onMouseMove={openMenu}
        onMouseOver={openMenu}
        onMouseLeave={handleMenuMouseLeave}
        onPointerEnter={openMenu}
        onPointerMove={openMenu}
        onPointerOver={openMenu}
        onPointerLeave={handleMenuPointerLeave}
        onClick={(e) => e.stopPropagation()}
        onCloseAutoFocus={(event) => {
          if (tabDismissedRef.current) {
            event.preventDefault();
            tabDismissedRef.current = false;
          }
        }}
        className="w-72 max-w-[90vw] rounded-b-xl rounded-t-none border border-border bg-popover p-0 shadow-lg"
      >
        <div className="border-b border-border bg-muted/30 p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchRef}
              aria-label="Search teams"
              placeholder="Search teams..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="h-8 rounded-md border-border bg-background pl-8 pr-2 text-sm shadow-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0"
            />
          </div>
        </div>

        <div ref={teamListRef} className="max-h-[380px] overflow-y-auto overflow-x-hidden py-0">
          {sortedAndFilteredTeams.length > 0 ? (
            sortedAndFilteredTeams.map((team, index) => {
              const isCurrentTeam = team.id === currentTeam?.id;

              return (
                <DropdownMenuItem
                  key={team.id}
                  data-testid={`team-menu-item-${team.id}`}
                  aria-current={isCurrentTeam ? 'true' : undefined}
                  onSelect={() => handleTeamSwitch(team)}
                  onKeyDown={(event) => {
                    if (event.key === 'Tab') {
                      if (event.shiftKey) {
                        event.preventDefault();
                        searchRef.current?.focus();
                      } else {
                        leaveMenuWithTab(event);
                      }
                      return;
                    }
                    if (index === 0 && event.key === 'ArrowUp') {
                      event.preventDefault();
                      searchRef.current?.focus();
                    }
                  }}
                  className={cn(
                    'flex min-h-0 w-full cursor-pointer items-center gap-2 whitespace-normal rounded-none px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
                    isCurrentTeam &&
                      'bg-primary/10 text-primary ring-1 ring-inset ring-primary/15 hover:bg-primary/15',
                  )}
                >
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-sm font-medium text-foreground',
                      isCurrentTeam && 'text-primary',
                    )}
                  >
                    {team.name}
                  </span>
                  {isCurrentTeam && (
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Check className="size-3.5" aria-hidden="true" />
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })
          ) : (
            <div className="px-3 py-8 text-center">
              <Search className="mx-auto mb-2 size-10 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No teams found</p>
              <p className="text-xs text-muted-foreground/70">Try a different search term</p>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default TeamMenu;
